import type { FC, ReactNode } from 'react'

/**
 * Music-theory Unicode glyphs that screen readers announce inconsistently
 * (or with the literal Unicode name like "music flat sign"). The accepted
 * fix is to give each glyph a `role="img"` wrapper with an `aria-label`
 * that names the concept in plain language — screen readers read the
 * aria-label *instead of* the glyph; sighted users see the glyph unchanged.
 *
 * Bowie convention (locked 2026-05-20): every pedagogical surface that
 * renders lexicon `copy.body` / `copy.tag` should use {@link TheoryGlyphs}
 * for the visible text. `title=` / `aria-label=` attribute values use
 * {@link ./spoken-text.ts spokenText} since those are consumed as strings.
 *
 * UI-status messages and chord-symbol displays (where the glyph IS the
 * chord-symbol, not pedagogical content) intentionally don't use this —
 * the chord-symbol renderers have their own pronunciation rules.
 */
const GLYPH_TO_SPOKEN: Record<string, string> = {
  '♭': 'flat',
  '♯': 'sharp',
  '°': 'diminished',
}

const GLYPHS = new Set(Object.keys(GLYPH_TO_SPOKEN))

export interface TheoryGlyphsProps {
  children: string
}

/**
 * Render music-theory text with assistive-tech-friendly glyph annotation.
 * Each ♭ / ♯ / ° glyph is wrapped in a `<span role="img" aria-label="...">`
 * so screen readers announce "flat" / "sharp" / "diminished" instead of
 * the Unicode character name. Sighted users see the original glyph;
 * text-selection / copy-paste preserve the glyph verbatim.
 *
 * Use anywhere lexicon `copy.body` / `copy.tag` (or other pedagogical
 * text) is rendered as visible text. Pairs with
 * {@link ./spoken-text.ts spokenText} for string-attribute contexts.
 *
 * @example
 *   <p><TheoryGlyphs>{copy.body}</TheoryGlyphs></p>
 *   // visible: "♭VII comes from Mixolydian"
 *   // screen reader: "flat VII comes from Mixolydian"
 */
export const TheoryGlyphs: FC<TheoryGlyphsProps> = ({ children }) => {
  const parts: ReactNode[] = []
  let buffer = ''
  let keyIdx = 0
  for (const char of children) {
    if (GLYPHS.has(char)) {
      if (buffer) {
        parts.push(buffer)
        buffer = ''
      }
      parts.push(
        <span key={keyIdx++} role="img" aria-label={GLYPH_TO_SPOKEN[char]}>
          {char}
        </span>
      )
    } else {
      buffer += char
    }
  }
  if (buffer) {
    parts.push(buffer)
  }
  return <>{parts}</>
}
